import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  EBAY_LISTING_BASE_TITLE_MAX_LENGTH,
  LISTING_TEMPLATE_ATTRIBUTES,
  LISTING_DESCRIPTION_MAX_LENGTH,
  LISTING_TEMPLATE_TARGET_LENGTH_DEFAULT,
  listingTemplateAttributeLabel,
  renderListingTemplateDescription,
  renderListingTemplateTitle,
  validateListingTemplate,
  type ListingTemplateAttribute,
  type ListingTemplateBlock,
  type ListingTemplateDescriptionBlock,
  type ListingTemplateSampleValues,
  type ListingTemplateSeparator,
  type ListingTemplateStatus,
  type MarketplaceListingTemplate,
  type SaveMarketplaceListingTemplateRequest,
} from '@tcvs/shared';
import { useMarketplaceApi } from './marketplace-api';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { reviewReturnHref, reviewTemplateEditorContextFrom } from './listing-template-navigation';

const SEPARATORS: ReadonlyArray<{ value: ListingTemplateSeparator; label: string }> = [
  { value: '', label: 'None' },
  { value: ' ', label: 'Space' },
  { value: ' - ', label: 'Dash' },
  { value: ' / ', label: 'Slash' },
  { value: ' | ', label: 'Pipe' },
];
const DORMANT_CARD_ATTRIBUTES = new Set<ListingTemplateAttribute>([
  'grader',
  'grade',
  'certNumber',
]);
const ACTIVE_LISTING_TEMPLATE_ATTRIBUTES = LISTING_TEMPLATE_ATTRIBUTES.filter(
  (attribute) => !DORMANT_CARD_ATTRIBUTES.has(attribute),
);

const DEFAULT_BLOCKS: ListingTemplateBlock[] = [
  attributeBlock('year', ''),
  attributeBlock('brand', ' '),
  attributeBlock('product', ' '),
  attributeBlock('name', ' - '),
  attributeBlock('cardNumber', ' '),
  attributeBlock('parallel', ' - '),
];

function blockId(): string {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function attributeBlock(
  attribute: ListingTemplateAttribute,
  separatorBefore: ListingTemplateSeparator = ' ',
): ListingTemplateBlock {
  return { blockId: blockId(), kind: 'ATTRIBUTE', attribute, separatorBefore };
}

function descriptionAttributeBlock(
  attribute: ListingTemplateAttribute,
  separatorBefore: ListingTemplateSeparator = ' ',
): ListingTemplateDescriptionBlock {
  return { blockId: blockId(), kind: 'ATTRIBUTE', attribute, separatorBefore };
}

function descriptionLineBreakBlock(lines: 1 | 2): ListingTemplateDescriptionBlock {
  return { blockId: blockId(), kind: 'LINE_BREAK', lines };
}

function newDraft(): SaveMarketplaceListingTemplateRequest {
  return {
    name: '',
    description: '',
    defaultDescription: '',
    descriptionPrompt: '',
    status: 'DRAFT',
    titleTargetLength: LISTING_TEMPLATE_TARGET_LENGTH_DEFAULT,
    titleBlocks: DEFAULT_BLOCKS.map((block) => ({ ...block, blockId: blockId() })),
    descriptionBlocks: [],
  };
}

export function draftFromTemplate(
  template: MarketplaceListingTemplate,
): SaveMarketplaceListingTemplateRequest {
  return {
    name: template.name,
    description: template.description,
    defaultDescription: template.defaultDescription,
    descriptionPrompt: template.descriptionPrompt,
    status: template.status,
    titleTargetLength: template.titleTargetLength,
    titleBlocks: template.titleBlocks
      .filter(
        (block) => block.kind !== 'ATTRIBUTE' || !DORMANT_CARD_ATTRIBUTES.has(block.attribute),
      )
      .map((block) => ({ ...block })),
    descriptionBlocks:
      (template.descriptionBlocks ?? []).length > 0
        ? (template.descriptionBlocks ?? [])
            .filter(
              (block) =>
                block.kind !== 'ATTRIBUTE' || !DORMANT_CARD_ATTRIBUTES.has(block.attribute),
            )
            .map((block) => ({ ...block }))
        : template.defaultDescription.trim() === ''
          ? []
          : [
              {
                blockId: blockId(),
                kind: 'TEXT',
                text: template.defaultDescription,
                separatorBefore: '',
              },
            ],
    expectedVersion: template.version,
  };
}

export function defaultListingTemplateSampleValues(): ListingTemplateSampleValues {
  return {
    year: '2024',
    brand: 'Panini',
    product: 'Prizm Football',
    subset: 'Base',
    cardNumber: '#123',
    name: 'Sample player',
    team: 'Sample team',
    parallel: 'Silver Prizm',
    rookie: 'RC',
    serialNumber: '/99',
    printRun: '/99',
    sku: '4398',
    shortCode: 'ABC123',
  };
}

export function moveListingTemplateBlock<T>(blocks: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= blocks.length || to < 0 || to >= blocks.length || from === to)
    return [...blocks];
  const next = [...blocks];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
}

export function listingTemplateStatusLabel(
  template: MarketplaceListingTemplate,
  defaultTemplateId: string | null,
  defaultCcgTemplateId: string | null = null,
): string {
  return `${template.status.toLowerCase()}${
    template.templateId === defaultTemplateId ? ' · default' : ''
  }${template.templateId === defaultCcgTemplateId ? ' · CCG default' : ''} · v${template.version}`;
}

export function ListingTemplatesPage(): JSX.Element {
  const marketplace = useMarketplaceApi();
  const location = useLocation();
  const auth = useAuth();
  const isAdmin = auth.claims !== null && hasAnyGroup(auth.claims, ['admin']);
  const reviewContext = useMemo(
    () => reviewTemplateEditorContextFrom(location.search),
    [location.search],
  );
  const [templates, setTemplates] = useState<MarketplaceListingTemplate[]>([]);
  const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(null);
  const [defaultCcgTemplateId, setDefaultCcgTemplateId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SaveMarketplaceListingTemplateRequest>(newDraft);
  const [sampleValues, setSampleValues] = useState<ListingTemplateSampleValues>(
    defaultListingTemplateSampleValues(),
  );
  const [listCollapsed, setListCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [titleFreeText, setTitleFreeText] = useState('');
  const [descriptionFreeText, setDescriptionFreeText] = useState('');
  const [draggedTitleIndex, setDraggedTitleIndex] = useState<number | null>(null);
  const [draggedDescriptionIndex, setDraggedDescriptionIndex] = useState<number | null>(null);
  const initializedReviewContext = useRef('');
  const [returnTemplateId, setReturnTemplateId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const templateResult =
      reviewContext === null
        ? await marketplace.listListingTemplates()
        : await marketplace.getListingReviewContext(reviewContext.checklistId);
    if (!templateResult.ok) setError(templateResult.message ?? templateResult.error);
    else {
      setTemplates(templateResult.data.templates);
      setDefaultTemplateId(templateResult.data.defaultTemplateId ?? null);
      if ('defaultCcgTemplateId' in templateResult.data)
        setDefaultCcgTemplateId(templateResult.data.defaultCcgTemplateId ?? null);
    }
    setLoading(false);
  }, [marketplace, reviewContext]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading || reviewContext === null) return;
    const key = `${reviewContext.checklistId}|${reviewContext.sourceTemplateId}|${reviewContext.mode}`;
    if (initializedReviewContext.current === key) return;
    initializedReviewContext.current = key;
    const source = templates.find(
      (template) => template.templateId === reviewContext.sourceTemplateId,
    );
    if (source === undefined) {
      setError(
        'That template is no longer active for this checklist. Return to review and refresh.',
      );
      return;
    }
    if (reviewContext.mode === 'edit') {
      setSelectedId(source.templateId);
      setDraft({
        ...draftFromTemplate(source),
        reviewContext: {
          checklistId: reviewContext.checklistId,
          sourceTemplateId: source.templateId,
          mode: 'EDIT',
        },
      });
      setReturnTemplateId(source.templateId);
    } else {
      setSelectedId(null);
      const clone = draftFromTemplate(source);
      delete clone.expectedVersion;
      setDraft({
        ...clone,
        name: reviewContext.cloneName ?? `${source.name} copy`,
        status: 'ACTIVE',
        reviewContext: {
          checklistId: reviewContext.checklistId,
          sourceTemplateId: source.templateId,
          mode: 'CLONE',
        },
      });
    }
    setSampleValues(defaultListingTemplateSampleValues());
  }, [loading, reviewContext, templates]);

  const selectedTemplate = templates.find((template) => template.templateId === selectedId);
  const preview = useMemo(
    () => renderListingTemplateTitle(draft.titleBlocks, sampleValues),
    [draft.titleBlocks, sampleValues],
  );
  const previewLength = preview.length;
  const descriptionPreview = useMemo(
    () => renderListingTemplateDescription(draft.descriptionBlocks ?? [], sampleValues),
    [draft.descriptionBlocks, sampleValues],
  );
  const descriptionPreviewLength = descriptionPreview.length;
  const descriptionExceeded = descriptionPreviewLength > LISTING_DESCRIPTION_MAX_LENGTH;
  const targetExceeded = previewLength > draft.titleTargetLength;
  const providerExceeded = previewLength > EBAY_LISTING_BASE_TITLE_MAX_LENGTH;
  const validation = validateListingTemplate(draft, sampleValues);
  const usedAttributes = useMemo(
    () => [
      ...new Set(
        [...draft.titleBlocks, ...(draft.descriptionBlocks ?? [])].flatMap((block) =>
          block.kind === 'ATTRIBUTE' ? [block.attribute] : [],
        ),
      ),
    ],
    [draft.descriptionBlocks, draft.titleBlocks],
  );

  function selectTemplate(template: MarketplaceListingTemplate): void {
    setSelectedId(template.templateId);
    setDraft({
      ...draftFromTemplate(template),
      ...(reviewContext === null
        ? {}
        : {
            reviewContext: {
              checklistId: reviewContext.checklistId,
              sourceTemplateId: template.templateId,
              mode: 'EDIT' as const,
            },
          }),
    });
    setReturnTemplateId(template.templateId);
    setSampleValues(defaultListingTemplateSampleValues());
    setError(null);
    setNotice(null);
  }

  function startNew(): void {
    setSelectedId(null);
    setDraft(newDraft());
    setSampleValues(defaultListingTemplateSampleValues());
    setError(null);
    setNotice(null);
  }

  function startClone(template: MarketplaceListingTemplate): void {
    const name = window.prompt('Name for the cloned template', `${template.name} copy`)?.trim();
    if (name === undefined || name === '') return;
    const clone = draftFromTemplate(template);
    delete clone.expectedVersion;
    setSelectedId(null);
    setDraft({
      ...clone,
      name,
      status: 'ACTIVE',
      ...(reviewContext === null
        ? {}
        : {
            reviewContext: {
              checklistId: reviewContext.checklistId,
              sourceTemplateId: template.templateId,
              mode: 'CLONE' as const,
            },
          }),
    });
    setReturnTemplateId(null);
    setError(null);
    setNotice('Cloned into a new draft. Make any changes, then create the template.');
  }

  function updateDraft(patch: Partial<SaveMarketplaceListingTemplateRequest>): void {
    setDraft((current) => ({ ...current, ...patch }));
    setNotice(null);
  }

  function addTitleAttribute(attribute: ListingTemplateAttribute): void {
    updateDraft({ titleBlocks: [...draft.titleBlocks, attributeBlock(attribute)] });
  }

  function addTitleFreeText(): void {
    const text = titleFreeText.trim();
    if (text === '') return;
    updateDraft({
      titleBlocks: [
        ...draft.titleBlocks,
        { blockId: blockId(), kind: 'TEXT', text, separatorBefore: ' ' },
      ],
    });
    setTitleFreeText('');
  }

  function updateTitleBlock(index: number, patch: Partial<ListingTemplateBlock>): void {
    updateDraft({
      titleBlocks: draft.titleBlocks.map((block, current) =>
        current === index ? ({ ...block, ...patch } as ListingTemplateBlock) : block,
      ),
    });
  }

  function updateDescriptionBlocks(blocks: ListingTemplateDescriptionBlock[]): void {
    updateDraft({
      descriptionBlocks: blocks,
      defaultDescription: renderListingTemplateDescription(blocks, {}),
    });
  }

  function addDescriptionAttribute(attribute: ListingTemplateAttribute): void {
    updateDescriptionBlocks([
      ...(draft.descriptionBlocks ?? []),
      descriptionAttributeBlock(attribute),
    ]);
  }

  function addDescriptionFreeText(): void {
    const text = descriptionFreeText.trim();
    if (text === '') return;
    updateDescriptionBlocks([
      ...(draft.descriptionBlocks ?? []),
      { blockId: blockId(), kind: 'TEXT', text, separatorBefore: ' ' },
    ]);
    setDescriptionFreeText('');
  }

  function addDescriptionLineBreak(lines: 1 | 2): void {
    updateDescriptionBlocks([...(draft.descriptionBlocks ?? []), descriptionLineBreakBlock(lines)]);
  }

  function updateDescriptionBlock(
    index: number,
    patch: Partial<ListingTemplateDescriptionBlock>,
  ): void {
    updateDescriptionBlocks(
      (draft.descriptionBlocks ?? []).map((block, current) =>
        current === index ? ({ ...block, ...patch } as ListingTemplateDescriptionBlock) : block,
      ),
    );
  }

  async function save(): Promise<void> {
    if (!validation.valid || descriptionExceeded) {
      setError(validation.errors[0] ?? 'Review the template before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    const response =
      selectedTemplate === undefined
        ? await marketplace.createListingTemplate(draft)
        : await marketplace.updateListingTemplate(selectedTemplate.templateId, draft);
    setSaving(false);
    if (!response.ok) {
      setError(response.message ?? response.error);
      return;
    }
    const saved = response.data.template;
    setTemplates((current) => [
      saved,
      ...current.filter((template) => template.templateId !== saved.templateId),
    ]);
    setSelectedId(saved.templateId);
    setDraft({
      ...draftFromTemplate(saved),
      ...(reviewContext === null
        ? {}
        : {
            reviewContext: {
              checklistId: reviewContext.checklistId,
              sourceTemplateId: saved.templateId,
              mode: 'EDIT' as const,
            },
          }),
    });
    setReturnTemplateId(saved.templateId);
    setNotice(`Saved version ${saved.version}.`);
  }

  async function updateDefault(enabled: boolean, scope?: 'CCG'): Promise<void> {
    if (selectedTemplate === undefined || savingDefault) return;
    setSavingDefault(true);
    setError(null);
    setNotice(null);
    const response = await marketplace.setDefaultListingTemplate(
      enabled ? selectedTemplate.templateId : null,
      scope,
    );
    setSavingDefault(false);
    if (!response.ok) {
      setError(response.message ?? response.error);
      return;
    }
    if (scope === 'CCG') {
      setDefaultCcgTemplateId(response.data.defaultCcgTemplateId ?? null);
      setNotice(
        enabled
          ? `${selectedTemplate.name} is now the trading-card-game default.`
          : 'Trading-card-game default cleared.',
      );
      return;
    }
    setDefaultTemplateId(response.data.defaultTemplateId ?? null);
    setNotice(
      enabled
        ? `${selectedTemplate.name} is now the default fallback.`
        : 'Default listing template cleared.',
    );
  }

  return (
    <div className="tcvs-listing-templates-page">
      <div className="tcvs-section-heading">
        <div>
          <h2>Listing templates</h2>
          <p className="tcvs-muted">
            Assemble reusable eBay US titles. Templates do not publish or change listings.
          </p>
        </div>
        <div className="tcvs-template-header-actions">
          {reviewContext !== null && returnTemplateId !== null ? (
            <Link
              className="tcvs-button"
              to={reviewReturnHref(reviewContext.returnTo, returnTemplateId)}
            >
              Back to card review
            </Link>
          ) : null}
          {isAdmin ? (
            <button type="button" className="tcvs-button tcvs-button-primary" onClick={startNew}>
              + New template
            </button>
          ) : null}
        </div>
      </div>

      {error !== null ? <div className="tcvs-alert tcvs-alert-danger">{error}</div> : null}
      {notice !== null ? <div className="tcvs-alert tcvs-alert-success">{notice}</div> : null}

      <div className="tcvs-template-workbench" data-list-collapsed={listCollapsed}>
        <aside className="tcvs-template-list" aria-label="Listing templates">
          {listCollapsed ? (
            <button
              type="button"
              className="tcvs-template-list-toggle"
              title="Expand template list"
              aria-label="Expand template list"
              onClick={() => setListCollapsed(false)}
            >
              ›
            </button>
          ) : (
            <>
              <div className="tcvs-template-panel-heading">
                <strong>Templates</strong>
                <div className="tcvs-template-panel-controls">
                  <span>{templates.length}</span>
                  <button
                    type="button"
                    className="tcvs-template-list-toggle"
                    title="Collapse template list"
                    aria-label="Collapse template list"
                    onClick={() => setListCollapsed(true)}
                  >
                    ‹
                  </button>
                </div>
              </div>
              {loading ? <p className="tcvs-muted">Loading...</p> : null}
              {!loading && templates.length === 0 ? (
                <p className="tcvs-muted">No templates yet.</p>
              ) : null}
              {templates.map((template) => (
                <button
                  key={template.templateId}
                  type="button"
                  className={
                    template.templateId === selectedId
                      ? 'tcvs-template-list-item tcvs-template-list-item-active'
                      : 'tcvs-template-list-item'
                  }
                  onClick={() => selectTemplate(template)}
                >
                  <strong>{template.name}</strong>
                  <span>
                    {listingTemplateStatusLabel(template, defaultTemplateId, defaultCcgTemplateId)}
                  </span>
                </button>
              ))}
            </>
          )}
        </aside>

        <div className="tcvs-template-editor">
          <div className="tcvs-template-editor-header">
            <div>
              <h3>{selectedTemplate === undefined ? 'New template' : selectedTemplate.name}</h3>
              <span className="tcvs-muted">
                {selectedTemplate === undefined
                  ? 'Unsaved draft'
                  : `Current version ${selectedTemplate.version}`}
              </span>
            </div>
            {selectedTemplate !== undefined ? (
              <button
                type="button"
                className="tcvs-button"
                onClick={() => startClone(selectedTemplate)}
              >
                Clone template
              </button>
            ) : null}
          </div>

          <section className="tcvs-template-section">
            <h4>Template settings</h4>
            <div className="tcvs-template-settings">
              <label className="tcvs-field">
                <span>Name</span>
                <input
                  value={draft.name}
                  maxLength={120}
                  onChange={(event) => updateDraft({ name: event.target.value })}
                  placeholder="eBay football singles"
                />
              </label>
              <label className="tcvs-field">
                <span>Status</span>
                <select
                  value={draft.status}
                  onChange={(event) =>
                    updateDraft({ status: event.target.value as ListingTemplateStatus })
                  }
                >
                  <option value="DRAFT">Draft</option>
                  <option value="ACTIVE">Active</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </label>
              <label className="tcvs-field">
                <span>Target title length</span>
                <input
                  type="number"
                  min={1}
                  max={EBAY_LISTING_BASE_TITLE_MAX_LENGTH}
                  value={draft.titleTargetLength}
                  onChange={(event) =>
                    updateDraft({ titleTargetLength: Number(event.target.value) })
                  }
                />
              </label>
              {isAdmin ? (
                <label className="tcvs-template-default-toggle">
                  <input
                    type="checkbox"
                    checked={
                      selectedTemplate !== undefined &&
                      selectedTemplate.templateId === defaultTemplateId
                    }
                    disabled={
                      selectedTemplate === undefined ||
                      selectedTemplate.status !== 'ACTIVE' ||
                      savingDefault
                    }
                    onChange={(event) => void updateDefault(event.target.checked)}
                  />
                  <span>
                    Default fallback
                    <small>Use when a checklist has no template assignment.</small>
                  </span>
                </label>
              ) : null}
              {isAdmin ? (
                <label className="tcvs-template-default-toggle">
                  <input
                    type="checkbox"
                    checked={
                      selectedTemplate !== undefined &&
                      selectedTemplate.templateId === defaultCcgTemplateId
                    }
                    disabled={
                      selectedTemplate === undefined ||
                      selectedTemplate.status !== 'ACTIVE' ||
                      savingDefault
                    }
                    onChange={(event) => void updateDefault(event.target.checked, 'CCG')}
                  />
                  <span>
                    Trading-card-game default
                    <small>Use for CCG cards when a checklist has no template assignment.</small>
                  </span>
                </label>
              ) : null}
              <label className="tcvs-field tcvs-template-description">
                <span>Description</span>
                <textarea
                  value={draft.description}
                  maxLength={1000}
                  rows={4}
                  onChange={(event) => updateDraft({ description: event.target.value })}
                  placeholder="Describe when this template should be used."
                />
              </label>
              <label className="tcvs-field tcvs-template-description">
                <span>AI description prompt</span>
                <textarea
                  value={draft.descriptionPrompt}
                  maxLength={2000}
                  rows={6}
                  onChange={(event) => updateDraft({ descriptionPrompt: event.target.value })}
                  placeholder="Tell the listing assistant how to write the card description."
                />
              </label>
            </div>
            <p className="tcvs-muted tcvs-template-note">
              Active makes this template available to a future draft workflow. It does not publish
              to eBay.
            </p>
          </section>

          <section className="tcvs-template-section">
            <h4>Title parts</h4>
            <div className="tcvs-template-attribute-palette">
              {ACTIVE_LISTING_TEMPLATE_ATTRIBUTES.map((attribute) => (
                <button
                  key={attribute}
                  type="button"
                  className="tcvs-template-token"
                  onClick={() => addTitleAttribute(attribute)}
                >
                  + {listingTemplateAttributeLabel(attribute)}
                </button>
              ))}
            </div>
            <div className="tcvs-template-free-text">
              <input
                value={titleFreeText}
                maxLength={80}
                onChange={(event) => setTitleFreeText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addTitleFreeText();
                  }
                }}
                placeholder="Add free text"
              />
              <button type="button" className="tcvs-button" onClick={addTitleFreeText}>
                Add text
              </button>
            </div>
            <div className="tcvs-template-canvas" aria-label="Title composition">
              {draft.titleBlocks.map((block, index) => (
                <div
                  key={block.blockId}
                  className="tcvs-template-block"
                  draggable
                  onDragStart={() => setDraggedTitleIndex(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (draggedTitleIndex !== null)
                      updateDraft({
                        titleBlocks: moveListingTemplateBlock(
                          draft.titleBlocks,
                          draggedTitleIndex,
                          index,
                        ),
                      });
                    setDraggedTitleIndex(null);
                  }}
                >
                  <span className="tcvs-template-drag" title="Drag to reorder" aria-hidden="true">
                    ::
                  </span>
                  <select
                    aria-label={`Separator before part ${index + 1}`}
                    value={index === 0 ? '' : block.separatorBefore}
                    disabled={index === 0}
                    onChange={(event) =>
                      updateTitleBlock(index, {
                        separatorBefore: event.target.value as ListingTemplateSeparator,
                      })
                    }
                  >
                    {SEPARATORS.map((separator) => (
                      <option key={separator.label} value={separator.value}>
                        {separator.label}
                      </option>
                    ))}
                  </select>
                  {block.kind === 'ATTRIBUTE' ? (
                    <strong>{listingTemplateAttributeLabel(block.attribute)}</strong>
                  ) : (
                    <input
                      aria-label={`Free text part ${index + 1}`}
                      value={block.text}
                      maxLength={80}
                      onChange={(event) => updateTitleBlock(index, { text: event.target.value })}
                    />
                  )}
                  <div className="tcvs-template-block-actions">
                    <button
                      type="button"
                      title="Move up"
                      aria-label={`Move part ${index + 1} up`}
                      disabled={index === 0}
                      onClick={() =>
                        updateDraft({
                          titleBlocks: moveListingTemplateBlock(
                            draft.titleBlocks,
                            index,
                            index - 1,
                          ),
                        })
                      }
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      title="Move down"
                      aria-label={`Move part ${index + 1} down`}
                      disabled={index === draft.titleBlocks.length - 1}
                      onClick={() =>
                        updateDraft({
                          titleBlocks: moveListingTemplateBlock(
                            draft.titleBlocks,
                            index,
                            index + 1,
                          ),
                        })
                      }
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      title="Remove"
                      aria-label={`Remove part ${index + 1}`}
                      onClick={() =>
                        updateDraft({
                          titleBlocks: draft.titleBlocks.filter((_, current) => current !== index),
                        })
                      }
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="tcvs-template-section">
            <div className="tcvs-template-section-heading">
              <div>
                <h4>Default description parts</h4>
                <span className="tcvs-muted">
                  Build card-specific default copy with attributes, text, and line breaks.
                </span>
              </div>
              <span
                className={
                  descriptionExceeded
                    ? 'tcvs-template-count tcvs-template-count-danger'
                    : 'tcvs-template-count'
                }
              >
                {descriptionPreviewLength}/{LISTING_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
            <div className="tcvs-template-attribute-palette">
              {ACTIVE_LISTING_TEMPLATE_ATTRIBUTES.map((attribute) => (
                <button
                  key={attribute}
                  type="button"
                  className="tcvs-template-token"
                  onClick={() => addDescriptionAttribute(attribute)}
                >
                  + {listingTemplateAttributeLabel(attribute)}
                </button>
              ))}
              <button
                type="button"
                className="tcvs-template-token"
                onClick={() => addDescriptionLineBreak(1)}
              >
                + New line
              </button>
              <button
                type="button"
                className="tcvs-template-token"
                onClick={() => addDescriptionLineBreak(2)}
              >
                + Blank line
              </button>
            </div>
            <div className="tcvs-template-free-text">
              <input
                value={descriptionFreeText}
                maxLength={LISTING_DESCRIPTION_MAX_LENGTH}
                onChange={(event) => setDescriptionFreeText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addDescriptionFreeText();
                  }
                }}
                placeholder="Add description text"
              />
              <button type="button" className="tcvs-button" onClick={addDescriptionFreeText}>
                Add text
              </button>
            </div>
            <div className="tcvs-template-canvas" aria-label="Default description composition">
              {(draft.descriptionBlocks ?? []).map((block, index) => (
                <div
                  key={block.blockId}
                  className="tcvs-template-block"
                  draggable
                  onDragStart={() => setDraggedDescriptionIndex(index)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (draggedDescriptionIndex !== null)
                      updateDescriptionBlocks(
                        moveListingTemplateBlock(
                          draft.descriptionBlocks ?? [],
                          draggedDescriptionIndex,
                          index,
                        ),
                      );
                    setDraggedDescriptionIndex(null);
                  }}
                >
                  <span className="tcvs-template-drag" title="Drag to reorder" aria-hidden="true">
                    ::
                  </span>
                  {block.kind === 'LINE_BREAK' ? (
                    <span className="tcvs-template-block-placeholder" aria-hidden="true" />
                  ) : (
                    <select
                      aria-label={`Separator before description part ${index + 1}`}
                      value={index === 0 ? '' : block.separatorBefore}
                      disabled={index === 0}
                      onChange={(event) =>
                        updateDescriptionBlock(index, {
                          separatorBefore: event.target.value as ListingTemplateSeparator,
                        })
                      }
                    >
                      {SEPARATORS.map((separator) => (
                        <option key={separator.label} value={separator.value}>
                          {separator.label}
                        </option>
                      ))}
                    </select>
                  )}
                  {block.kind === 'ATTRIBUTE' ? (
                    <strong>{listingTemplateAttributeLabel(block.attribute)}</strong>
                  ) : block.kind === 'TEXT' ? (
                    <input
                      aria-label={`Description text part ${index + 1}`}
                      value={block.text}
                      maxLength={LISTING_DESCRIPTION_MAX_LENGTH}
                      onChange={(event) =>
                        updateDescriptionBlock(index, { text: event.target.value })
                      }
                    />
                  ) : (
                    <strong>{block.lines === 2 ? 'Blank line' : 'New line'}</strong>
                  )}
                  <div className="tcvs-template-block-actions">
                    <button
                      type="button"
                      title="Move up"
                      aria-label={`Move description part ${index + 1} up`}
                      disabled={index === 0}
                      onClick={() =>
                        updateDescriptionBlocks(
                          moveListingTemplateBlock(draft.descriptionBlocks ?? [], index, index - 1),
                        )
                      }
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      title="Move down"
                      aria-label={`Move description part ${index + 1} down`}
                      disabled={index === (draft.descriptionBlocks ?? []).length - 1}
                      onClick={() =>
                        updateDescriptionBlocks(
                          moveListingTemplateBlock(draft.descriptionBlocks ?? [], index, index + 1),
                        )
                      }
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      title="Remove"
                      aria-label={`Remove description part ${index + 1}`}
                      onClick={() =>
                        updateDescriptionBlocks(
                          (draft.descriptionBlocks ?? []).filter((_, current) => current !== index),
                        )
                      }
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {descriptionExceeded ? (
              <p className="tcvs-error">
                Shorten the sample description by{' '}
                {descriptionPreviewLength - LISTING_DESCRIPTION_MAX_LENGTH} characters to save.
              </p>
            ) : null}
          </section>
          <div className="tcvs-template-save-bar">
            <button
              type="button"
              className="tcvs-button tcvs-button-primary"
              disabled={saving || providerExceeded || descriptionExceeded}
              onClick={() => void save()}
            >
              {saving
                ? 'Saving...'
                : selectedTemplate === undefined
                  ? 'Create template'
                  : 'Save new version'}
            </button>
          </div>
        </div>

        <aside className="tcvs-template-preview">
          <div className="tcvs-template-panel-heading">
            <strong>Live preview</strong>
            <span
              className={
                providerExceeded
                  ? 'tcvs-template-count tcvs-template-count-danger'
                  : targetExceeded
                    ? 'tcvs-template-count tcvs-template-count-warning'
                    : 'tcvs-template-count'
              }
            >
              {previewLength}/{EBAY_LISTING_BASE_TITLE_MAX_LENGTH}
            </span>
          </div>
          <div className="tcvs-template-preview-title">
            {preview === '' ? (
              <span className="tcvs-muted">Add title parts to preview.</span>
            ) : (
              preview
            )}
          </div>
          <div className="tcvs-template-length-track" aria-hidden="true">
            <span
              className={
                providerExceeded
                  ? 'tcvs-template-length-fill tcvs-template-length-danger'
                  : targetExceeded
                    ? 'tcvs-template-length-fill tcvs-template-length-warning'
                    : 'tcvs-template-length-fill'
              }
              style={{
                width: `${Math.min(100, (previewLength / EBAY_LISTING_BASE_TITLE_MAX_LENGTH) * 100)}%`,
              }}
            />
          </div>
          <p
            className={
              providerExceeded
                ? 'tcvs-error'
                : targetExceeded
                  ? 'tcvs-template-warning'
                  : 'tcvs-muted'
            }
          >
            {providerExceeded
              ? `Shorten by ${previewLength - EBAY_LISTING_BASE_TITLE_MAX_LENGTH} characters to save.`
              : targetExceeded
                ? `${previewLength - draft.titleTargetLength} characters above your target.`
                : `${draft.titleTargetLength - previewLength} characters remain before your target.`}
          </p>

          <div className="tcvs-template-panel-heading">
            <strong>Default description</strong>
            <span
              className={
                descriptionExceeded
                  ? 'tcvs-template-count tcvs-template-count-danger'
                  : 'tcvs-template-count'
              }
            >
              {descriptionPreviewLength}/{LISTING_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
          <div className="tcvs-template-preview-description">
            {descriptionPreview === '' ? (
              <span className="tcvs-muted">Add description parts to preview.</span>
            ) : (
              descriptionPreview
            )}
          </div>

          <h4>Sample card</h4>
          <p className="tcvs-muted tcvs-template-note">
            Edit these values to test long and optional card data.
          </p>
          <div className="tcvs-template-sample-fields">
            {usedAttributes.map((attribute) => (
              <label className="tcvs-field" key={attribute}>
                <span>{listingTemplateAttributeLabel(attribute)}</span>
                <input
                  value={sampleValues[attribute] ?? ''}
                  onChange={(event) =>
                    setSampleValues((current) => ({
                      ...current,
                      [attribute]: event.target.value,
                    }))
                  }
                />
              </label>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
